import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ButtonType } from '../../../hdi-components/src/shared/enums/button-type.enums';
import TeaserType from '../../../hdi-components/src/shared/enums/teaser.type.enums';
import ViewportType from '../../../hdi-components/src/shared/enums/viewport-type.enums';

import MenuItem, {
  FlyoutData,
} from '../../../hdi-components/src/shared/interfaces/menuItems.interface';
import {
  CaasFooter,
  CaasLayout,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { PodcastItems } from '../../../hdi-components/src/shared/interfaces/podcast-item.interface';
import { CaasContentService } from '../services/caas-content.service';
import { NavigationService } from '../services/navigation.service';
import { LoadingService } from '../services/loading.service';
import { Video } from '../../../hdi-components/src/shared/interfaces/video.interface';
import { ModalType } from 'hdi-components';
import { Router, Scroll } from '@angular/router';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'hdi-pw-migration';
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() viewportDTM: ViewportType = ViewportType.desktop;
  ModalType = ModalType;
  video?: Video;
  showModal: boolean = false;

  private destroy$ = new Subject<void>();

  visibleTeasers = 3;
  buttonType = ButtonType;
  teaserType = TeaserType;

  // Data holders
  podcasts: PodcastItems[] = [];
  metaMenu: MenuItem[] = [];
  flyoutData!: FlyoutData[];
  footer: CaasFooter = {} as CaasFooter;
  datasets: CaasLayout[] = [];
  events: CaasLayout[] = [];
  media: CaasLayout[] = [];
  mainMenu: MenuItem[] = [];
  isLoading = true;
  loadingSubscription?: Subscription;

  constructor(
    private caasContentServices: CaasContentService,
    private navigationServices: NavigationService,
    private loadingService: LoadingService,
    private router: Router,
    private viewportScroller: ViewportScroller
  ) {}

  ngOnInit() {
    this.subscribeToLoadingState();
    this.loadData();
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'openVideoModal',
        this.handleVideoModal.bind(this)
      );
    }
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(filter((event): event is Scroll => event instanceof Scroll))
      .subscribe(this.handleScrollEvent.bind(this));
  }

  private handleScrollEvent(event: Scroll): void {
    setTimeout(() => {
      if (event.position) {
        this.viewportScroller.scrollToPosition(event.position);
      } else if (event.anchor) {
        this.viewportScroller.scrollToAnchor(event.anchor);
      } else {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    }, 300);
  }

  private subscribeToLoadingState(): void {
    this.loadingSubscription = this.loadingService.loading$.subscribe(
      (loading: boolean) => {
        this.isLoading = loading;

        if (!loading) {
          // Trigger scrolling when loading is complete
          this.subscribeToRouterEvents();
        }
      }
    );

    if (typeof window !== 'undefined') {
      window.addEventListener('openVideoModal', (event: Event) => {
        if (event instanceof CustomEvent) {
          this.video = event.detail;
          this.showModal = true;
        }
      });
    }
  }

  private loadData() {
    this.navigationServices
      .getMainNavigation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: MenuItem[]) => (this.mainMenu = data),
        error: (error) => this.logError('Main Navigation', error),
      });

    this.navigationServices
      .getFlyoutData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: FlyoutData[]) => (this.flyoutData = data),
        error: (error) => this.logError('Flyout:', error),
      });

    this.caasContentServices
      .getMetaNavigation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: MenuItem[]) => (this.metaMenu = data),
        error: (error) => this.logError('Meta Navigation', error),
      });

    this.caasContentServices
      .getFooter()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (content: CaasFooter | null) =>
          (this.footer = content as CaasFooter),
        error: (error) => this.logError('Footer Content', error),
      });

    this.caasContentServices
      .getDataset()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CaasLayout[]) => (this.datasets = data),
        error: (error) => this.logError('Dataset Content', error),
      });

    this.caasContentServices
      .getMedia()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CaasLayout[]) => (this.media = data),
        error: (error) => this.logError('Media Content', error),
      });
  }

  private handleVideoModal(event: Event): void {
    if (event instanceof CustomEvent) {
      this.video = event.detail;
      this.showModal = true;
    }
  }

  private logError(context: string, error: Error): void {
    console.error(`Error fetching ${context}:`, error);
  }

  closeModal(event: boolean): void {
    this.showModal = event;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingSubscription?.unsubscribe();

    if (typeof window !== 'undefined') {
      window.removeEventListener(
        'openVideoModal',
        this.handleVideoModal.bind(this)
      );
    }
  }
}
